import { useState, useCallback } from 'react';
import StopCard from './StopCard';
import useTripStore from '../../context/TripStore';

/**
 * Collapsible list of added stops with drag-to-reorder
 */
function StopsList() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const places = useTripStore((state) => state.places ?? []);
  const reorderPlaces = useTripStore((state) => state.reorderPlaces);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add visual feedback
    e.currentTarget.style.opacity = '0.5';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e, targetIndex) => {
      e.preventDefault();
      if (draggedIndex !== null && draggedIndex !== targetIndex) {
        reorderPlaces(draggedIndex, targetIndex);
      }
      setDraggedIndex(null);
    },
    [draggedIndex, reorderPlaces]
  );

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
  }, []);

  if (places.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      {/* Header with collapse toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-1.5"
      >
        <span className="text-xs font-medium text-gray-700">
          Your stops ({places.length})
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Collapsible content */}
      {isExpanded && (
        <div
          className="space-y-1.5"
          onDragEnd={handleDragEnd}
        >
          {places.map((place, index) => (
            <StopCard
              key={place.id}
              place={place}
              index={index}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {/* Summary when collapsed */}
      {!isExpanded && places.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {places.slice(0, 3).map((place) => (
            <span
              key={place.id}
              className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600 truncate max-w-20"
            >
              {place.name}
            </span>
          ))}
          {places.length > 3 && (
            <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
              +{places.length - 3} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default StopsList;
